// Insel-Entry: mountet das Lager in #lager-root.
//
// Eigener Bundle (dist/lager.js), unabhängig vom Vanilla-Kern und von der
// Dashboard-Insel. Die gesamte Kontaktfläche des Moduls zum bestehenden DOM
// sind der Tab-Knopf und dieser Wurzelknoten — und seit L10 die
// Abschluss-Maske, die sich über `window.lagerAbschlussOeffnen` rufen lässt.
import { mount, unmount } from 'svelte';
import Lager from './Lager.svelte';
import AbgangPruefen from './views/AbgangPruefen.svelte';
import EingangBuchen from './views/EingangBuchen.svelte';

// Mount hinter dem Ready-Gate (A2), wie bei der Dashboard-Insel: Vor
// `app:ready` ist der lokale Bestand noch nicht geladen. Anders als das
// Dashboard rechnet das Lager zwar nichts beim Mounten, aber die Regel gilt
// hier genauso, sobald in L2 die Datenschicht dazukommt.
//
// Bewusst KEIN Warten auf die Freischaltung: Die Insel darf montiert sein,
// ohne sichtbar zu sein — das Panel bleibt `hidden`, bis switchTab('lager')
// es öffnet, und das lässt lager-gate.js nur für freigeschaltete Konten zu.
// Ein Mount, der erst auf das Profil wartet, hätte beim ersten Öffnen des
// Tabs einen leeren Rahmen gezeigt.
//
// ⚠ Dieser Mount trägt seit L10 auch die Tokens der Abschluss-Maske
// (`.lg-tokens` in Lager.svelte, per `css: 'injected'` erst beim Mount im
// <head>). Er darf deshalb nicht hinter die Freischaltung wandern.
const target = document.getElementById('lager-root');
if (target) {
  const start = () => mount(Lager, { target });
  if (window.AppState?.appReady) start();
  else document.addEventListener('app:ready', start, { once: true });
}

/**
 * Öffnet die Abschluss-Maske (A7/L10) — Konzepte/Lager/abgang-pruefen.md.
 *
 * Asynchron, weil der Vorschlag den Bestand braucht und das Journal erst beim
 * ersten Öffnen des Lagers geladen wird: Ungeladen stünde jede Zeile in
 * „Prüfen", weil jeder Bestand null wäre.
 *
 * Liefert `false`, wenn es nichts zu prüfen gibt (Backtag ohne Zutaten) — der
 * Aufrufer zeigt dann die bisherige Rückfrage. Die Maske entscheidet also nie
 * selbst, dass ein Backtag ohne Rückfrage abgeschlossen wird.
 *
 * @param {any} bt
 * @param {{ hinweis?: string|null,
 *           onbestaetigt: (abgang: any[]) => void,
 *           onabbruch?: () => void }} opts
 * @returns {Promise<boolean>} ob die Maske offen ist
 */
window.lagerAbschlussOeffnen = async function lagerAbschlussOeffnen(bt, opts) {
  if (!bt || typeof window.lagerAbgangVorschlag !== 'function') return false;
  if (typeof window.lagerJournalGeladen === 'function' && !window.lagerJournalGeladen()) {
    await window.lagerJournalLaden();
  }
  // Schon offen (etwa Wiederbetreten des Backzettels während der Maske):
  // keine zweite darüberlegen.
  if (document.querySelector('[data-lager-abschluss]')) return true;
  const vorschlag = window.lagerAbgangVorschlag(bt);
  if (!vorschlag.length) return false;

  // Unter `body`, nicht in #lager-root: Das Panel ist beim Abschluss verborgen.
  const host = document.createElement('div');
  host.className = 'lg-tokens';
  host.dataset.lagerAbschluss = '';
  document.body.appendChild(host);

  let app = null;
  const schliessen = () => {
    if (app) { unmount(app); app = null; }
    host.remove();
  };

  app = mount(AbgangPruefen, {
    target: host,
    props: {
      bt,
      vorschlag,
      hinweis: opts?.hinweis ?? null,
      onabbruch: () => { schliessen(); opts?.onabbruch?.(); },
      onbestaetigt: (abgang, aliasse) => {
        schliessen();
        opts.onbestaetigt(abgang);
        // Zuordnungen merken braucht die Cloud. Das hält den Abschluss nicht
        // auf: Er ist in diesem Moment schon geschehen, und eine vergessene
        // Zuordnung kostet beim nächsten Backtag nur eine Rückfrage.
        for (const a of aliasse || []) {
          Promise.resolve(window.zutatAliasAnlegen?.(a.zutatId, a.alias)).then((ok) => {
            if (!ok) window.notify?.({ level: 'warn', text: window.t('lager_abschluss_merken_fehler', { name: a.alias }) });
          });
        }
      },
    },
  });
  return true;
};

/**
 * Öffnet die Eingangsmaske von AUSSERHALB des Lagers — aus der Zutatentabelle
 * eines Backtags (Konzepte/Lager/backtag-deckung.md, B-3). Die Maske lebt
 * sonst als Kind von Lager.svelte in #panel-lager, und das Panel ist im
 * Backtag-Tab verborgen; ein Sprung in den Lager-Tab ließe den Bäcker nach dem
 * Buchen im Lager stehen. Deshalb wie die Abschluss-Maske: unter `body`, in
 * einem `.lg-tokens`-Host. Die Maske bringt ihren eigenen Container-Bruch mit
 * (`.lg-host` trägt container-type), sie misst also das Fenster.
 *
 * Nach dem Buchen feuert das Journal `lager:journal-geaendert`; die Tabelle
 * rechnet darauf neu — es braucht keinen Rückruf.
 *
 * @param {string|null} [vorwahl] zutat.id, mit der die Maske aufgeht
 * @returns {Promise<boolean>} ob die Maske offen ist
 */
window.lagerEingangOeffnen = async function lagerEingangOeffnen(vorwahl) {
  if (typeof window.lagerJournalGeladen === 'function' && !window.lagerJournalGeladen()) {
    await window.lagerJournalLaden?.();
  }
  if (document.querySelector('[data-lager-eingang]')) return true;

  const host = document.createElement('div');
  host.className = 'lg-tokens';
  host.dataset.lagerEingang = '';
  document.body.appendChild(host);

  let app = null;
  const schliessen = () => {
    if (app) { unmount(app); app = null; }
    host.remove();
  };
  app = mount(EingangBuchen, {
    target: host,
    props: { vorwahl: vorwahl || null, onschliessen: schliessen },
  });
  return true;
};

/**
 * Wechselt ins Lager und öffnet dort das Detail einer Zutat („Im Lager
 * ansehen" im Blatt der Zutatentabelle). Lager.svelte hört auf das Ereignis
 * und setzt seine offene Zutat — die Navigation bleibt Zustand der Insel,
 * keine Route (Plan 17.6).
 * @param {string|null} zutatId
 */
window.lagerZutatOeffnen = function lagerZutatOeffnen(zutatId) {
  if (!zutatId) return;
  window.switchTab?.('lager');
  document.dispatchEvent(new CustomEvent('lager:zutat-oeffnen', { detail: { zutatId } }));
};
